import React from 'react';
import { DateWrapper } from '@trucktrax/trucktrax-common';
import { getMixDesignItem, ProductLine, TicketDto } from '@trucktrax/trucktrax-ts-common';
import { READYMIX_STRING } from '../../../../constants/appConstants';
import { RowInfo } from './ticketListConfigurations.types';
import styles from '../TicketList.module.css';
import voidedTicket from '../../../../assets/img/voidedTicket.png';

// NOTE (clogan review, DEV-4153): keep this module a pure leaf — no component
// imports. The Received column (a connected component) lives beside its cell
// in geotrax/incomingMaterials/MaterialReceivedCell.tsx; importing it here
// closed an import cycle back to Main through every config.

export const voidedAccessor = (isVoided?: boolean, content?: any) => {
  if (!isVoided) {
    return `${content}`;
  }

  return <div className={styles.voidedCellStyle}>{`${content}`}</div>;
};

export const ticketNumberAccessor = (row: RowInfo) => (
  <div className={row.voided ? styles.voidedCellStyle : undefined}>
    {`${row.ticketNumber}`}
    {row.voided && <img src={voidedTicket} alt="voided" />}
  </div>
);

export const ticketedTimestampAccessor = (row: RowInfo) => {
  const date = new DateWrapper(row.ticketedTimestamp).toUSDateString();
  return voidedAccessor(row?.voided, date);
};

export const truckAliasAccessor = (row: RowInfo) =>
  voidedAccessor(row?.voided, row.externalInfo?.truckAlias ?? '');

export const driverNameAccessor = (row: RowInfo) =>
  voidedAccessor(row?.voided, row?.externalInfo?.driverName ?? '');

export const customerAccessor = (row: RowInfo) =>
  voidedAccessor(row?.voided, row?.order?.customerName ?? '');

export const orderNumberAccessor = (row: RowInfo) =>
  voidedAccessor(row?.voided, row?.order?.orderNumber ?? 0);

export const addressAccessor = (row: RowInfo) => voidedAccessor(row?.voided, row.address ?? '');

export const loadingPlantAccessor = (row: RowInfo) =>
  voidedAccessor(row?.voided, row.externalInfo?.plantName ?? '');

export const mixDesignProductNumberAccessor = (row: RowInfo) =>
  voidedAccessor(row?.voided, getMixDesignItem(row)?.productNumber ?? '');

// DEV-5225: the label shown for a material source's product line — 'Ready-mix'
// as elsewhere in the app, nothing for None. Mirrors adminUtil's
// formatProductLineString, which this leaf module can't import (adminUtil
// imports components).
export const productLineLabel = (productLine?: ProductLine) => {
  if (!productLine || productLine === ProductLine.None) {
    return '';
  }
  return productLine === ProductLine.ReadyMix ? READYMIX_STRING : productLine;
};

export const productLineAccessor = (row: RowInfo) =>
  voidedAccessor(row?.voided, productLineLabel(row.externalInfo?.plantProductLine));

export const haulerAccessor = (row: RowInfo) => voidedAccessor(row?.voided, row.hauler ?? '');

// DEV-5225: the Incoming Materials list also searches the Hauler column. Product
// line has its own filter (IncomingProductLineFilter), so search leaves it out.
export const incomingMaterialsSearchText = (ticket: TicketDto) => [ticket.hauler];
